import React, { useState, useEffect } from 'react';
import { BarChart3, TrendingUp, TrendingDown, AlertTriangle, Users, Clock, Zap } from 'lucide-react';
import { 
  ResponsiveContainer, 
  ComposedChart, 
  Bar, 
  Line, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  Legend,
  Cell
} from 'recharts';
import Card from '../ui/Card';
import { formatHours } from '../../utils/helpers';


const OverallHoursStats = ({ 
  totalRemainingHours, 
  remainingHoursStudentCount,
  weeklyHoursData = [],
  growthRate,
  lowHoursStudents = []
}) => {
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    // 延迟渲染以确保容器尺寸已稳定
    const timer = setTimeout(() => setMounted(true), 500);
    return () => clearTimeout(timer);
  }, []);

 // 计算增长趋势图标
  const getGrowthIcon = () => {
    return growthRate >= 0 ? TrendingUp : TrendingDown;
 };

  // 计算增长趋势颜色
  const getGrowthColor = () => {
    return growthRate >= 0 ? 'text-green-500' : 'text-red-500';
  };

  const chartData = [...weeklyHoursData].reverse().map(item => {
    const hours = item.hours || 0;
    const students = item.student_count || item.active_students || 0;
    const ratio = students > 0 ? (hours / students) : 0;
    
    // 格式化月份为 2601 格式
    const formatToYYMM = (monthStr) => {
      const match = monthStr.match(/(\d{4})年(\d{1,2})月/);
      if (match) {
        const year = match[1].substring(2);
        const month = match[2].padStart(2, '0');
        return `${year}${month}`;
      }
      return monthStr;
    };
    
    return {
      name: item.month,
      displayMonth: formatToYYMM(item.month),
      hours: parseFloat(hours.toFixed(1)),
      students: students,
      ratio: parseFloat(ratio.toFixed(2))
    };
  });

  // 自定义 Tooltip
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white p-2.5 border border-gray-100 shadow-xl rounded-lg text-[11px]">
          <p className="font-black text-gray-700 mb-1.5 border-b pb-1">{label}</p>
          <div className="space-y-1">
            <div className="flex items-center justify-between gap-4">
              <div className="flex items-center gap-1.5">
                <div className="w-2 h-2 rounded-full bg-[#3182CE]"></div>
                <span className="text-gray-500 font-bold">消耗课时</span>
              </div>
              <span className="font-black text-[#2D3748]">{payload[0].value} 节</span>
            </div>
            <div className="flex items-center justify-between gap-4">
              <div className="flex items-center gap-1.5">
                <div className="w-2 h-2 rounded-full bg-[#38B2AC]"></div>
                <span className="text-gray-500 font-bold">动课学生</span>
              </div>
              <span className="font-black text-[#2D3748]">{payload[1].value} 人</span>
            </div>
            <div className="flex items-center justify-between gap-4 pt-1 border-t mt-1">
              <div className="flex items-center gap-1.5">
                <div className="w-2 h-2 rounded-full bg-[#E53E3E]"></div>
                <span className="text-gray-500 font-bold">月消比 (人均消课)</span>
              </div>
              <span className="font-black text-[#E53E3E]">{payload[2].value} 节/人·月</span>
            </div>
            <p className="text-[9px] text-gray-400 mt-1 italic leading-tight">
              * 表示平均每位学生在该月内消耗的课时总数
            </p>


          </div>
        </div>
      );
    }
    return null;
  };


  return (
    <Card className="p-3">
      <div className="flex items-center gap-2 mb-3">
        <BarChart3 size={16} className="text-[#3182CE]" />
        <h2 className="font-bold text-base text-[#2D3748]">总体课时统计</h2>
      </div>
      
      <div className="grid grid-cols-2 gap-3 mb-3">
        {/* 总剩余课时数 */}
        <div className="bg-blue-50/50 p-2 rounded-lg border border-blue-100 flex flex-col justify-center">
          <div className="text-[11px] text-blue-600 font-bold mb-0.5">总剩余课时</div>
          <div className="text-xl font-black text-blue-700 leading-none">{formatHours(totalRemainingHours)}</div>
        </div>
        
        {/* 课时学生数 */}
        <div className="bg-green-50/50 p-2 rounded-lg border border-green-100 flex flex-col justify-center">
          <div className="text-[11px] text-green-600 font-bold mb-0.5">课时学生数</div>
          <div className="text-xl font-black text-green-700 leading-none">{remainingHoursStudentCount}</div>
        </div>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        
        {/* 考勤动态（最近1年） - 独占一行 */}

        <div className="bg-white p-3 rounded-lg border border-[#EDF2F7] md:col-span-2">
          <div className="flex justify-between items-center mb-4">
            <div className="text-xs text-[#4A5568] font-black flex items-center gap-1.5 uppercase tracking-wider">
              <Zap size={12} className="text-orange-500" />
              考勤动态（最近1年）

            </div>
            <div className="flex gap-3">
              <div className="flex items-center gap-1">
                <div className="w-2 h-2 rounded-full bg-[#3182CE]"></div>
                <span className="text-[10px] text-gray-500 font-bold">课时</span>
              </div>
              <div className="flex items-center gap-1">
                <div className="w-2 h-2 rounded-full bg-[#38B2AC]"></div>
                <span className="text-[10px] text-gray-500 font-bold">学生</span>
              </div>
              <div className="flex items-center gap-1">
                <div className="w-2 h-2 rounded-full bg-[#E53E3E]"></div>
                <span className="text-[10px] text-gray-500 font-bold">月消比</span>
              </div>


            </div>
          </div>
          
          <div className="h-[220px] w-full">
            {mounted && (
              <ResponsiveContainer width="100%" height={220} minWidth={0} minHeight={0}>
                <ComposedChart
                  data={chartData}
                  margin={{ top: 5, right: 0, left: -20, bottom: 0 }}
                >
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#F7FAFC" />
                  <XAxis 
                    dataKey="displayMonth" 
                    axisLine={false} 
                    tickLine={false} 
                    tick={{ fill: '#A0AEC0', fontSize: 10, fontWeight: 700 }}
                    angle={-45}
                    textAnchor="end"
                    height={40}
                    interval={0}
                  />

                  <YAxis 
                    yAxisId="left"
                    axisLine={false} 
                    tickLine={false} 
                    tick={{ fill: '#A0AEC0', fontSize: 10, fontWeight: 700 }}
                  />
                  <YAxis 
                    yAxisId="right"
                    orientation="right"
                    axisLine={false} 
                    tickLine={false} 
                    tick={{ fill: '#E53E3E', fontSize: 10, fontWeight: 700 }}
                    domain={[0, 'auto']}
                  />


                  <Tooltip content={<CustomTooltip />} />
                  <Bar 
                    yAxisId="left"
                    dataKey="hours" 
                    fill="#3182CE" 
                    radius={[4, 4, 0, 0]} 
                    barSize={12}
                    name="消耗课时"
                  />
                  <Bar 
                    yAxisId="left"
                    dataKey="students" 
                    fill="#38B2AC" 
                    radius={[4, 4, 0, 0]} 
                    barSize={12}
                    name="动课学生"
                  />


                  <Line 
                    yAxisId="right"
                    type="monotone" 
                    dataKey="ratio" 
                    stroke="#E53E3E" 
                    strokeWidth={2}
                    dot={{ r: 3, fill: '#E53E3E', strokeWidth: 2, stroke: '#fff' }}
                    activeDot={{ r: 5, strokeWidth: 0 }}
                    name="月消比"


                  />
                </ComposedChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>

        
        {/* 环比上一周期增长率 */}
        <div className="bg-white p-3 rounded-lg border border-[#EDF2F7] md:col-span-2">
          <div className="flex justify-between items-center">
            <div>
              <div className="text-xs text-[#718096] font-bold uppercase">环比周期增长</div>
              <div className="flex items-center gap-1 mt-0.5">
                <span className={`text-xl font-black ${getGrowthColor()}`}>
                  {growthRate >= 0 ? '+' : ''}{growthRate}%
                </span>
                <span className="text-[11px] text-gray-400">较上期</span>
              </div>
            </div>
            <div className={`${getGrowthColor()} bg-opacity-10 p-1.5 rounded-full`}>
              {React.createElement(getGrowthIcon(), { size: 16 })}
            </div>
          </div>
        </div>
        
        {/* 课时不足学生预警列表 */}
        <div className="bg-white p-3 rounded-lg border border-[#EDF2F7] md:col-span-2">
          <div className="flex items-center gap-2 mb-2">
            <AlertTriangle size={14} className="text-orange-500" />
            <div className="text-xs text-[#4A5568] font-black uppercase tracking-wider">课时不足预警</div>
            <span className="text-[10px] text-[#718096]">（≤ 2节）</span>
          </div>
          
          {lowHoursStudents.length > 0 ? (
            <div className="space-y-1">
              {/* 表头 */}
              <div className="grid grid-cols-2 gap-2 text-xs font-medium text-[#718096] px-2 py-1.5 bg-[#F7FAFC] rounded-t">
                <div>学生姓名</div>
                <div className="text-right">剩余课时</div>
              </div>
              
              {/* 数据行 */}
              {lowHoursStudents.map((item, index) => (
                <div
                  key={item.student_id}
                  className={`grid grid-cols-2 gap-2 text-[13px] p-1.5 rounded ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'}`}
                >
                  <div className="text-[#2D3748] font-bold truncate">{item.student_name}</div>
                  <div className={`text-right font-black ${item.remaining_hours <= 0 ? 'text-red-600' : 'text-orange-600'}`}>
                    {formatHours(item.remaining_hours)}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-4 text-sm text-[#718096]">
              暂无课时不足的学生
            </div>
          )}
        </div>
      </div>
    </Card>
  );
};

export default OverallHoursStats;